import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../store";
import { IApplicationState } from "../../../store";
import { 
  IAuction, 
  ICapacityReturnRequest, 
  ICapacityReturnResponse, 
  ISourceAuction 
} from "../../../services";
import { SubmitCapacityReturnAction } from "../../../store/Returns/ActionCreator";
import { toast } from "react-toastify";

/**
 * Custom hook to manage the submission process for capacity returns
 */
export function useReturnsSubmission(auction?: IAuction) {
  const dispatch = useAppDispatch();
  
  // Submission state
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionResult, setSubmissionResult] = useState<ICapacityReturnResponse | null>(null);
  const [selectedSourceAuctions, setSelectedSourceAuctions] = useState<Record<string, ISourceAuction>>({});
  
  // Redux selectors
  const isSubmitting = useSelector((state: IApplicationState) => state.returns.isSubmitting);
  const returnSubmissionResponse = useSelector((state: IApplicationState) => state.returns.returnSubmissionResponse);
  const errorMessage = useSelector((state: IApplicationState) => state.returns.errorMessage);
  
  // Effect to handle submission response
  useEffect(() => {
    // Only show success toast when submission just completed (isSubmitting changed from true to false)
    // and we have a response
    if (!isSubmitting && returnSubmissionResponse) {
      setSubmissionResult(returnSubmissionResponse);
      // Show success toast
      toast.success("Your return request has been successfully submitted.", { 
        autoClose: 5000, 
        draggable: true,
        position: "bottom-right" 
      });
    }
  }, [isSubmitting, returnSubmissionResponse]);

  // Effect to handle submission errors
  useEffect(() => {
    if (errorMessage && !isSubmitting) {
      // Show error toast for submission errors
      toast.error(`There was an error submitting your return request: ${errorMessage}`, { 
        autoClose: false, 
        draggable: true,
        position: "bottom-right" 
      });
    }
  }, [errorMessage, isSubmitting]);
  
  /**
   * Handles the return submission workflow, including validation
   */
  const handleSubmitReturn = (
    quantities: Record<string, string>,
    rowData: ISourceAuction[],
    editingAuctionId: string | null,
    activeInputRef: React.RefObject<HTMLInputElement | null>,
    getExceedingCapacityAuctions: () => Array<{auctionId: string, value: string, maxCapacity: number}>,
    setQuantities: (quantities: Record<string, string>) => void,
    setEditingAuctionId: (auctionId: string | null) => void,
    setShowExceedsCapacityModal: (show: boolean) => void,
    setShowNoQuantityModal: (show: boolean) => void,
    setShowConfirmation: (show: boolean) => void
  ) => {
    // Create a local copy of quantities that we can update
    let updatedQuantities = { ...quantities };
    let updatedSelectedAuctions: Record<string, ISourceAuction> = {};

    // If user is still editing a quantity, try to save it first if it's valid
    if (editingAuctionId && activeInputRef.current) {
      const value = activeInputRef.current.value;
      if (value && value.trim() !== "") {
        // Always update our quantities with the current value, even if it exceeds capacity
        // This ensures we don't lose the value
        updatedQuantities = {
          ...updatedQuantities,
          [editingAuctionId]: value,
        };
      }
    }

    // Check for quantities that exceed capacity
    const exceedingCapacityAuctions = getExceedingCapacityAuctions();

    // If any quantity exceeds capacity, show error modal and preserve ALL values
    if (exceedingCapacityAuctions.length > 0) {
      // First, preserve all quantities (including the exceeding ones)
      setQuantities({ ...updatedQuantities });

      // Set the editing auction ID to the one with the exceeding value
      setEditingAuctionId(exceedingCapacityAuctions[0].auctionId);

      // After a short delay to ensure state updates, show the error modal
      setTimeout(() => {
        setShowExceedsCapacityModal(true);
      }, 50);

      return;
    }

    // Validate the quantities first - ensure they are positive numbers
    const hasInvalidQuantities = Object.entries(updatedQuantities).some(
      ([_, value]) => {
        const numValue = parseFloat(value);
        return isNaN(numValue) || numValue <= 0;
      }
    );

    if (hasInvalidQuantities) {
      alert("Please enter a valid quantity for the capacity to return.");
      return;
    }

    // Check if we have any quantities to submit
    if (Object.keys(updatedQuantities).length === 0) {
      setShowNoQuantityModal(true);
      return;
    }

    // Format the data for submission and capture selected auctions
    const resellRequests = Object.entries(updatedQuantities)
      .map(([auctionId, quantityStr]) => {
        const quantity = parseFloat(quantityStr);

        // Skip items with zero or empty quantities
        if (!quantity || isNaN(quantity)) return null;

        // Find the corresponding auction data
        const auctionData = rowData.find(
          (auction: ISourceAuction) => auction.auctionId === auctionId
        );

        // Store the full auction object for later use in the confirmation modal
        if (auctionData) {
          updatedSelectedAuctions[auctionId] = auctionData;
        }

        return {
          sourceAuctionId: auctionId,
          targetAuctionId: auction?.identification,
          quantity: quantity,
          corridor: auctionData?.corridor,
        };
      })
      .filter(Boolean); // Remove null entries

    if (resellRequests.length === 0) {
      setShowNoQuantityModal(true);
      return;
    }

    // Update state with our local copy to ensure UI is consistent
    setQuantities(updatedQuantities);
    setSelectedSourceAuctions(updatedSelectedAuctions);

    // Show confirmation popup
    setShowConfirmation(true);
  };

  /**
   * Confirms the submission after the user has reviewed the confirmation dialog
   */
  const confirmSubmitReturn = async (
    quantities: Record<string, string>,
    bookCode: string,
    setShowConfirmation: (show: boolean) => void) => {
    try {
      // Get the first selected auction to extract data from
      const sourceAuctionValues = Object.values(selectedSourceAuctions);
      if (sourceAuctionValues.length === 0) {
        throw new Error("No source auctions selected");
      }

      const sourceAuction = sourceAuctionValues[0];
      const totalReturnCapacity = Object.values(quantities).reduce(
        (sum, val) => sum + (parseFloat(val) || 0),
        0
      );

      // Prepare the request payload
      const request: ICapacityReturnRequest = {
        targetAuctionIdentification: auction?.identification || "",
        targetAuctionName: auction?.auctionName || "",
        sourceAuctionIdentification: sourceAuction.auctionId,
        sourceAuctionName: sourceAuction.auctionName,
        returnCapacity: totalReturnCapacity,
        bookCode,
        exchangeCode: auction?.exchangeCode || "UNKNOWN exchange code",
        exchangeId: auction?.exchangeId || -1,
        corridor: sourceAuction.corridor || "",
      };

      dispatch(SubmitCapacityReturnAction(request));

      // Close confirmation dialog
      setShowConfirmation(false);
    } catch (error) {
      console.error("Error preparing return request:", error);
      const errorMsg = error instanceof Error
        ? error.message
        : "An unknown error occurred";

      setSubmissionError(errorMsg);

      // Show error toast instead of modal
      toast.error(`Error preparing return request: ${errorMsg}`, { 
        autoClose: 5000, 
        draggable: true 
      });
    }
  };

  return {
    submissionError,
    setSubmissionError,
    submissionResult,
    setSubmissionResult,
    selectedSourceAuctions,
    setSelectedSourceAuctions,
    isSubmitting,
    returnSubmissionResponse,
    errorMessage,
    handleSubmitReturn,
    confirmSubmitReturn
  };
}